import React, { useEffect, useState } from "react";
import {
  search_value,
  Filter,
  BellPlus,
  Edit,
  Trash2,
  CheckCircle,
  XCircle,
  BellRing,
  Search,
  RotateCwIcon,
  Banknote,
  Eye,
} from "lucide-react";
import Pagination from "../../components/Pagination";
import { usePayments } from "../../hooks/usePayments";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment/moment";
import { toast } from "react-toastify";
import { useResolvedPath } from "react-router-dom";
import Skeleton from "../../components/ui/Skeleton";
import DeleteModal from "../../components/ui/DeleteModal";
import ActionButton from "../../components/ui/ActionButton";
import CustomModal from "../../components/ui/CustomModal";

function PaymentManagement() {
  const [paymentsList, setPaymentsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState("view");
  const [statusFilter, setStatusFilter] = useState({
    // payment_type: "BROADCAST",
    filter: JSON.stringify({
      field: "payment_by",
      value: "cash",
    }),
  });
  const [permission, setPermission] = useState({});
  const [updateMode, setUpdateMode] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [pageData, setPageData] = useState({
    currentPage: 1,
    totalPages: Math.ceil(paymentsList.length / 10),
    showing: 10,
    total: 0,
  });

  const [sortField, setSortField] = useState("transaction_date");
  const [sortDirection, setSortDirection] = useState("desc");

  const { fetchPayments } = usePayments({
    filters: { ...statusFilter, search_value: searchQuery },
    pagination: { page: pageData.currentPage, limit: 10 },
    sort: { field: sortField, direction: sortDirection },
  });

  const paymentsQuery = useQuery({
    queryKey: [
      "payments",
      pageData.currentPage,
      statusFilter,
      searchQuery,
      sortField,
      sortDirection,
    ],
    queryFn: fetchPayments,
    keepPreviousData: true,
  });

  const handleSort = (field) => {
    if (field === sortField) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPageData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  };

  const openModal = (payment, mode) => {
    setSelectedPayment(payment);
    setModalMode(mode);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedPayment(null);
  };

  const handlePageChange = (page) => {
    setPageData((prev) => ({
      ...prev,
      currentPage: page,
    }));
  };

  useEffect(() => {
    if (paymentsQuery.data && !paymentsQuery.isLoading) {
      const response = paymentsQuery.data;
      setPaymentsList(response?.data?.data || []);
      setPageData(() => ({
        currentPage: response?.data?.pagination?.currentPage || 1,
        totalPages: response?.data?.pagination?.lastPage || 1,
        total: response?.data?.pagination?.total || 0,
        showing: response?.data?.pagination?.showing || 10,
      }));
    }
  }, [
    paymentsQuery.data,
    pageData.showing,
    pageData.currentPage,
    paymentsQuery.isLoading,
  ]);

  const handleUpdate = (payment) => {
    openModal(payment, "edit");
    setUpdateMode(true);
  };

  const handleDelete = {
    // onDelete: (payment) =>
    //   mutation.deletePayment.mutate(payment._id, {
    //     onSuccess: async (data) => {
    //       toast.success(data.message || "Payment Deleted");
    //       setDeleteModal(false);
    //     },
    //   }),
    onClose: () => setDeleteModal(false),
    onOpen: (payment) => {
      setDeleteModal(true);
      setSelectedPayment(payment);
    },
  };

  // const mutation = {
  //   deletePayment: useMutation({
  //     mutationFn: async (id) => await deletePayment(id),
  //   }),
  // };

  return (
    <div>
      <main>
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h1 className="text-2xl font-bold">Payment Management</h1>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchQuery}
                onChange={handleSearch}
                className="pl-10 pr-4 py-2 border rounded-md w-10/12 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-400" />
              <select
                value={statusFilter.filter}
                onChange={(e) =>
                  setStatusFilter({
                    ...statusFilter,
                    filter: e.target.value,
                  })
                }
                className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option
                  value={JSON.stringify({
                    field: "payment_by",
                    value: "cash",
                  })}
                >
                  Cash Purchase
                </option>
                <option
                  value={JSON.stringify({
                    field: "payment_by",
                    value: "points",
                  })}
                >
                  Points Purchase
                </option>
              </select>
              <button
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter({
                    filter: JSON.stringify({
                      field: "payment_by",
                      value: "cash",
                    }),
                  });
                  setPageData((prev) => ({
                    ...prev,
                    currentPage: 1,
                  }));
                }}
                className="flex items-center justify-center bg-gray-100 text-gray-600 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
              >
                <RotateCwIcon className="h-4 w-4" />
                <span className="ml-2">Reset</span>
              </button>
            </div>
          </div>

          {paymentsQuery.isLoading ? (
            <Skeleton.Card header rows={10} />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 text-left">
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("amount")}
                    >
                      Amount
                      {sortField === "amount" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("user_name")}
                    >
                      User Name
                      {sortField === "user_name" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("user_phone")}
                    >
                      User Phone
                      {sortField === "user_phone" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>
                    <th
                      className="px-4 py-3 text-sm font-medium text-gray-500 border-b cursor-pointer select-none"
                      onClick={() => handleSort("transaction_date")}
                    >
                      Purchase Date
                      {sortField === "transaction_date" && (
                        <span className="ml-1">
                          {sortDirection === "asc" ? "▲" : "▼"}
                        </span>
                      )}
                    </th>

                    <th className="px-4 py-3 text-sm font-medium text-gray-500 border-b">
                      View
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paymentsList.length > 0 ? (
                    paymentsList.map((payment) => (
                      <tr
                        key={payment._id}
                        className="hover:bg-gray-50 border-b"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-8 w-8 bg-gray-200 rounded-full flex items-center justify-center">
                              <Banknote className="h-4 w-4 text-gray-600" />
                            </div>
                            <div className="ml-3">
                              <div className="font-medium text-gray-900 capitalize md:uppercase">
                                {payment.amount || "-"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {payment.user_name || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {payment.user_phone || "-"}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {moment(payment.transaction_date).format("MMMM Do YYYY, h:mm:ss a")}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          <button
                            className="text-blue-400 hover:text-blue-500"
                            onClick={() => openModal(payment, "view")}
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={8}
                        className="px-4 py-8 text-center text-gray-500"
                      >
                        No payments found matching your search criteria
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <Pagination
            currentPage={pageData.currentPage}
            onPageChange={handlePageChange}
            showing={pageData.showing}
            totalPages={pageData.totalPages}
            total={pageData.total}
          />
        </div>
      </main>
      <CustomModal
        data={selectedPayment}
        isOpen={showModal}
        onClose={closeModal}
        title={"Payment Data"}
        exclude_fields={["_id", "user_id", "user", "product_data", "product_id", "__v"]}
      />
      <DeleteModal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onDelete={() => {}}
        title="Payment"
      />
    </div>
  );
}

export default PaymentManagement;
